import posthog from 'posthog-js'

import { isAnalyticsConfigured, isGroupAnalyticsEnabled } from './config'

/**
 * Properties attached to every subsequent event via PostHog super properties.
 *
 * This is how BR-37 is satisfied without every call site restating the same six
 * fields. Super properties persist in PostHog's own storage, so a stale value
 * would ride along after a workspace switch — callers must re-register on change
 * and unregister when a value goes away.
 */
export type SuperProperties = {
  org_id?: number
  role?: string
  /**
   * BR-12's industry, as the codename the onboarding profile stores
   * (`retail`, `real_estate`, …) — the same string that goes out in
   * `POST /onboarding/profile/`, so a client event and the platform's own
   * record of the workspace break down by the identical value.
   */
  industry?: string
  company_size?: string
  locale?: string
  env?: string
  app_version?: string
  /**
   * Which journey the events belong to — `core` here, one per app later.
   * Registered rather than passed per event: it is constant for the whole
   * attempt, and repeating it at ~15 call sites is 15 chances to forget it.
   */
  scope?: string
  /** How the journey was entered. Constant per journey, same reasoning. */
  entry?: string
}

/**
 * The pseudonymous id this browser is known by before anyone has signed up.
 *
 * Travels to the API as `X-Analytics-Id` on unauthenticated requests, so the
 * platform can record its own pre-signup events against the same person this
 * client is recording against; {@link identifyAccount} then joins the two
 * halves — everything stamped with this id is re-attributed to the account.
 *
 * Undefined when analytics is unconfigured: there is then no person to stitch
 * onto and no events to stitch.
 */
export const analyticsDistinctId = (): string | undefined => {
  if (!isAnalyticsConfigured) return undefined

  return posthog.get_distinct_id()
}

/**
 * Identifies the account.
 *
 * `distinctId` is the numeric account id — pseudonymous, per BR-44. Never the
 * email address: it is personal data, and PostHog stores `distinct_id`
 * unredacted by design.
 *
 * This call is also what stitches the anonymous pre-signup session onto the
 * person, which is the whole reason the signup screens are instrumented — the
 * funnel would otherwise start after the account already exists.
 */
export const identifyAccount = (
  distinctId: string,
  personProperties?: Record<string, unknown>
): void => {
  if (!isAnalyticsConfigured) return

  posthog.identify(distinctId, personProperties)
}

/** Adds or replaces super properties. Undefined values are skipped. */
export const registerProperties = (properties: SuperProperties): void => {
  if (!isAnalyticsConfigured) return

  const defined = Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined)
  )
  if (Object.keys(defined).length === 0) return

  posthog.register(defined)
}

/**
 * Drops super properties. Needed when a value stops applying — leaving a stale
 * `org_id` registered would mis-attribute every later event to a workspace the
 * user is no longer in.
 */
export const unregisterProperties = (keys: (keyof SuperProperties)[]): void => {
  if (!isAnalyticsConfigured) return

  keys.forEach((key) => posthog.unregister(key))
}

/**
 * Associates the account with its organization.
 *
 * Only runs when group analytics is paid for. Without it the organization
 * dimension still reaches the dashboard as event properties — what is missing is
 * native per-organization uniqueness in funnels and retention, which a SQL
 * insight over `properties.org_id` stands in for.
 */
export const associateOrganization = (
  orgId: number,
  properties?: Record<string, unknown>
): void => {
  if (!isAnalyticsConfigured || !isGroupAnalyticsEnabled) return

  posthog.group('organization', String(orgId), properties)
}

/**
 * Forgets the current person. Called on sign-out so the next account on this
 * browser does not inherit the previous one's identity or super properties.
 */
export const resetIdentity = (): void => {
  if (!isAnalyticsConfigured) return

  posthog.reset()
}
